import React from 'react'
import styled from '@emotion/styled'
import Panel from '@components/common/Panel'
import YSpacing from '@components/common/YSpacing'
import Button from '@components/common/form/Button'
import LabelInfo from '@components/common/form/LabelInfo'
import { Schedule, ScheduleRule, DayOfWeek } from '../types'
import ScheduleHeader from './ScheduleHeader'
import DefaultTimesForm from './DefaultTimesForm'
import WeekCycleRulesEditor from './WeekCycleRulesEditor'
import services from '@services'

interface Props {
  schedules: Schedule[]
  selectedScheduleId: string | null
  rules: ScheduleRule[]
  isDirty: boolean
  overlapError: string | null
  onSelectSchedule: (id: string) => void
  onCreateSchedule: () => void
  onDeleteSchedule: (id: string) => void
  onUpdateSchedule: (partial: Partial<Schedule>) => void
  onAddRule: (cycleWeek: number, dayOfWeek: DayOfWeek) => void
  onUpdateRule: (id: string, partial: Partial<ScheduleRule>) => void
  onRemoveRule: (id: string) => void
  onSave: () => Promise<void>
  onDiscard: () => void
}

const ScheduleConfigForm: React.FC<Props> = ({
  schedules,
  selectedScheduleId,
  rules,
  isDirty,
  overlapError,
  onSelectSchedule,
  onCreateSchedule,
  onDeleteSchedule,
  onUpdateSchedule,
  onAddRule,
  onUpdateRule,
  onRemoveRule,
  onSave,
  onDiscard,
}) => {
  const selectedSchedule = schedules.find((s) => s.id === selectedScheduleId)

  return (
    <div>
      {/* Schedule Selector */}
      <Panel width="100%" heading="Schedules">
        <LabelInfo label="Each schedule covers a date range. Ranges must not overlap." />
        <YSpacing height="8px" />
        <ScheduleList>
          {schedules.map((s) => (
            <ScheduleTab
              key={s.id}
              isSelected={s.id === selectedScheduleId}
              onClick={() => onSelectSchedule(s.id)}
              data-testid={`schedule-tab-${s.id}`}
            >
              <ScheduleTabLabel>
                {s.name ? `${s.name} · ` : ''}
                {s.effectiveFrom} — {s.effectiveUntil || 'ongoing'}
              </ScheduleTabLabel>
              {s.cycleLengthWeeks !== null ? (
                <ScheduleTabMeta>{s.cycleLengthWeeks}w cycle</ScheduleTabMeta>
              ) : (
                <ScheduleTabMeta>ad hoc</ScheduleTabMeta>
              )}
            </ScheduleTab>
          ))}
        </ScheduleList>
        <YSpacing height="8px" />
        <ButtonRow>
          <Button size="sm" label="+ New Schedule" onClick={onCreateSchedule} />
          {selectedScheduleId && schedules.length > 1 && (
            <Button
              size="sm"
              label="Delete Selected"
              onClick={async () => {
                const confirmed =
                  await services.UIService.ConfirmationModal.show({
                    title: 'Delete Schedule',
                    text: 'Are you sure you want to delete this schedule? This will also remove all of its recurring rules and overrides.',
                  })
                if (confirmed) {
                  onDeleteSchedule(selectedScheduleId)
                }
              }}
              backgroundColor="#E57373"
            />
          )}
        </ButtonRow>
        {overlapError && <ErrorText>{overlapError}</ErrorText>}
      </Panel>

      {selectedSchedule && (
        <>
          <YSpacing height="8px" />

          <Panel width="100%" heading="Schedule Configuration">
            <ScheduleHeader
              schedule={selectedSchedule}
              onUpdate={onUpdateSchedule}
            />
          </Panel>

          <YSpacing height="8px" />

          <Panel width="100%" heading="Schedule-Level Default Times">
            <DefaultTimesForm
              schedule={selectedSchedule}
              onUpdate={onUpdateSchedule}
            />
          </Panel>

          <YSpacing height="8px" />

          <Panel width="100%" heading="Recurring Service Days">
            <LabelInfo label="Click a day to toggle service. Expand a row for per-day time overrides." />
            <YSpacing height="6px" />
            <WeekCycleRulesEditor
              schedule={selectedSchedule}
              rules={rules}
              onAddRule={onAddRule}
              onUpdateRule={onUpdateRule}
              onRemoveRule={onRemoveRule}
            />
          </Panel>

          <YSpacing height="10px" />

          <ButtonRow>
            <Button
              size="sm"
              label="Save Changes"
              onClick={onSave}
              disabled={!isDirty || !!overlapError}
            />
            {isDirty && (
              <Button
                size="sm"
                label="Discard Changes"
                onClick={async () => {
                  const confirmed =
                    await services.UIService.ConfirmationModal.show({
                      title: 'Discard Changes',
                      text: 'Are you sure you want to discard all unsaved changes?',
                    })
                  if (confirmed) {
                    onDiscard()
                  }
                }}
                backgroundColor="#9E9E9E"
              />
            )}
          </ButtonRow>
        </>
      )}
    </div>
  )
}

export default ScheduleConfigForm

// ── Styled Components ──

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const ScheduleTab = styled.div<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 10px;
  border: 1.5px solid
    ${({ isSelected }) => (isSelected ? '#1976D2' : '#E0E0E0')};
  border-radius: 4px;
  background: ${({ isSelected }) => (isSelected ? '#E3F2FD' : '#FAFAFA')};
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: #90caf9;
  }
`

const ScheduleTabLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #212121;
`

const ScheduleTabMeta = styled.span`
  font-size: 10px;
  color: #757575;
  white-space: nowrap;
  margin-left: 6px;
`

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`

const ErrorText = styled.p`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 8px;
  font-weight: 500;
`
